import { ArrowUpRight } from '@untitled-ui/icons-react/build/cjs';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Address from 'src/app/MyQuote/AddressDetails/Address';
import AutoCompleteGoogle from 'src/components/AutoCompleteGoogle/AutoCompleteGoogle';
import ButtonCmp from 'src/components/ButtonCmp';
import InputText from 'src/components/InputText/InputText';
import MapBox from 'src/components/MapBox/MapBox';
import SelectBox from 'src/components/SelectBox/SelectBox';
import { fetchShippers, searchConsignees, searchShippers } from 'src/redux/address.slice';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { createAddress } from 'src/services/AddressBookServices';
import { getDuplicateAddressMessage } from 'src/utils/addressBookDuplicate';
import WalToast from 'src/utils/WalToast';

export interface IAddressData {
    id?: number | null;
    companyName: string;
    fullAddress: string;
    address1?: string;
    city?: string;
    state?: string;
    stateCode?: string;
    country?: string;
    countryCode?: string;
    postal?: string;
    latitude?: string;
    longitude?: string;
    streetNumber?: string;
    timezone?: string;
}

interface AddressSelectionSectionProps {
    isShipper: boolean;
    label: string;
    selectedAddressId: number | null;
    addressData: IAddressData | null;
    isCreatingNew: boolean;
    onAddressSelect: (id: number, addressData: IAddressData) => void;
    onCreateNew: () => void;
    onCancelNew: () => void;
    onNewAddressChange: (data: IAddressData) => void;
    onAddressSaved: (id: number, addressData: IAddressData) => void;
    errors: any;
    isLoading: boolean;
}

const AddressSelectionSection = ({
    isShipper,
    label,
    selectedAddressId,
    addressData,
    isCreatingNew,
    onAddressSelect,
    onCreateNew,
    onCancelNew,
    onNewAddressChange,
    onAddressSaved,
    errors,
    isLoading,
}: AddressSelectionSectionProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { shippers, consignees, shipperOptions, consigneeOptions } = useAppSelector((state) => state.addresses);
    const { googleKey } = useAppSelector((state) => state.basic);
    const [searchInput, setSearchInput] = useState('');
    const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showErrors, setShowErrors] = useState(false);
    // The backend's duplicate name+address rejection. Kept separately from `showErrors` because it
    // carries a message and is cleared by editing either half of what clashed, not by re-submitting.
    const [duplicateMessage, setDuplicateMessage] = useState<string | null>(null);

    const addressDataRef = useRef(addressData);
    useEffect(() => {
        addressDataRef.current = addressData;
    }, [addressData]);

    useEffect(() => {
        setShowErrors(false);
    }, [isCreatingNew]);

    // Format addresses for dropdown with subLabels
    const formattedOptions = useMemo(() => {
        const baseOptions = isShipper ? shipperOptions : consigneeOptions;
        const fullList = isShipper ? shippers : consignees;

        return baseOptions.map((option: any) => {
            const addressItem = fullList?.find((item: any) => item.id === option.value);

            return {
                ...option,
                subLabel: addressItem?.fullAddress || '',
            };
        });
    }, [isShipper, shipperOptions, consigneeOptions, shippers, consignees]);

    // Get the selected option for the dropdown
    const selectedOption = useMemo(() => {
        if (!selectedAddressId) return null;

        // First try to find in formatted options
        const foundInFormatted = formattedOptions.find((opt: any) => opt.value === selectedAddressId);
        if (foundInFormatted) return foundInFormatted;

        // If not found (due to search filtering), get from full list
        const fullList = isShipper ? shippers : consignees;
        const fullData = fullList?.find((item: any) => item.id === selectedAddressId);

        if (fullData) {
            return {
                value: fullData.id,
                label: fullData.companyName,
                subLabel: fullData.fullAddress || '',
            };
        }

        if (addressData && (addressData.companyName || addressData.fullAddress)) {
            return {
                value: selectedAddressId,
                label: addressData.companyName || addressData.fullAddress,
                subLabel: addressData.fullAddress || '',
            };
        }

        return null;
    }, [selectedAddressId, formattedOptions, isShipper, shippers, consignees, addressData]);

    // Handle search input change
    const handleInputChange = (inputValue: string) => {
        setSearchInput(inputValue);

        if (isShipper) {
            dispatch(searchShippers(inputValue));
        } else {
            dispatch(searchConsignees(inputValue));
        }
    };

    // Handle dropdown selection
    const handleDropdownChange = (selected: any) => {
        if (!selected) {
            onCancelNew();

            return;
        }

        const fullList = isShipper ? shippers : consignees;
        const fullData = fullList?.find((item: any) => item.id === selected.value);

        if (fullData) {
            onAddressSelect(selected.value, {
                id: fullData.id,
                companyName: fullData.companyName || '',
                fullAddress: fullData.fullAddress || '',
                address1: fullData.address1 || '',
                city: fullData.city || '',
                state: fullData.state || '',
                stateCode: fullData.stateCode || '',
                country: fullData.country || '',
                countryCode: fullData.countryCode || '',
                postal: fullData.postal || '',
                latitude: fullData.latitude || '',
                longitude: fullData.longitude || '',
                streetNumber: fullData.streetNumber || '',
                timezone: fullData.timezone || '',
            });
        }
    };

    // Handle address autocomplete change (for new address)
    const handleAutocompleteChange = (value: any) => {
        if (!value || !addressData) return;

        if (typeof value === 'object' && value.fullAddress !== undefined) {
            onNewAddressChange({
                ...addressData,
                companyName: addressDataRef.current?.companyName ?? addressData.companyName,
                fullAddress: value.fullAddress ?? '',
                address1: value.address1 ?? '',
                city: value.city ?? '',
                state: value.state ?? '',
                stateCode: value.stateCode ?? '',
                country: value.country ?? '',
                countryCode: value.countryCode ?? '',
                postal: value.postal ?? '',
                latitude: value.latitude?.toString() ?? '',
                longitude: value.longitude?.toString() ?? '',
                streetNumber: value.streetNumber ?? '',
                timezone: value.timezone ?? '',
            });

            if (showErrors) {
                setShowErrors(false);
            }

            setDuplicateMessage(null);
        }
    };

    // Handle address input change
    const handleAddressInputChange = (value: string) => {
        if (addressData) {
            onNewAddressChange({
                ...addressData,
                fullAddress: value,
            });
            setDuplicateMessage(null);
        }
    };

    // Handle company name change
    const handleCompanyNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        if (value !== '' && value.trim() === '') return;

        if (addressData) {
            onNewAddressChange({
                ...addressData,
                companyName: value,
            });
        }

        if (showErrors && value.trim()) {
            setShowErrors(false);
        }

        setDuplicateMessage(null);
    };

    // Handle Address modal data update
    const handleAddressModalSave = (data: any) => {
        if (addressData) {
            onNewAddressChange({
                ...addressData,
                ...data,
            });
        }
    };

    // Save new address to address book
    const handleSaveNewAddress = async () => {
        // Show errors when user attempts to save
        setShowErrors(true);

        if (!addressData || !addressData.companyName || !addressData.fullAddress || !addressData.streetNumber) {
            // No toast — the reason rides on the address field's own alert icon (client, 2026-10-09).
            // See isAddressIncomplete below for why that field is the one that carries it.
            return;
        }

        setIsSaving(true);
        setDuplicateMessage(null);

        try {
            const payload = {
                addressType: isShipper ? 'SHIPPER' : 'CONSIGNEE',
                companyName: addressData.companyName,
                fullAddress: addressData.fullAddress,
                address1: addressData.address1 || '',
                city: addressData.city || '',
                state: addressData.state || '',
                stateCode: addressData.stateCode || '',
                country: addressData.country || '',
                countryCode: addressData.countryCode || '',
                postal: addressData.postal || '',
                latitude: addressData.latitude || '',
                longitude: addressData.longitude || '',
                streetNumber: addressData.streetNumber || '',
            };

            const response = await createAddress(payload);

            if (response?.data?.id) {
                WalToast.success(isShipper ? t('Orders.Shipper_saved') : t('Orders.Consignee_saved'));

                // Refresh the address list in redux
                dispatch(fetchShippers());

                // Notify parent with the new address ID
                onAddressSaved(response.data.id, {
                    ...addressData,
                    id: response.data.id,
                });
            }
        } catch (error: any) {
            console.error('Error saving address:', error);

            const duplicate = getDuplicateAddressMessage(error, t('AddressBook.Duplicate_Address_Exists'));

            if (duplicate) {
                // Shown on the name field's alert icon rather than as a toast (client, 2026-10-09).
                setDuplicateMessage(duplicate);
            } else {
                WalToast.error(error?.response?.data?.message || t('Orders.Address_save_failed'));
            }
        } finally {
            setIsSaving(false);
        }
    };

    // The user picked/typed something, but it did not resolve to a street-level address (no street
    // number) — the only save failure reachable from the Save button, which is disabled until both
    // the name and an address string exist.
    const isAddressIncomplete = !!addressData?.fullAddress && !addressData?.streetNumber;

    // Map component for new address form
    const MapComponent = useMemo(
        () => (
            <MapBox
                draggable={false}
                address={addressData && addressData.latitude && addressData.longitude ? addressData : null}
                id="editOrderMap"
                onReverseAddressFunc={handleAutocompleteChange}
                className="rounded-lg bg-white h-[200px] border border-utilityGray200"
            />
        ),
        [addressData?.latitude, addressData?.longitude]
    );

    // If creating new address, show the new address form
    if (isCreatingNew) {
        return (
            <div className="space-y-3">
                <label className="form_label block mb-1.5">
                    {label} <span className="text-red-500">*</span>
                </label>

                <div className="bg-gray50 border border-utilityGray200 rounded-xl p-3 space-y-3">
                    {/* Company Name */}
                    <InputText
                        label={isShipper ? t('Orders.Shipper_Name') : t('Orders.Consignee_Name')}
                        labelClassName="block mb-1.5"
                        inputName="companyName"
                        placeholder={isShipper ? t('Orders.Enter_shipper_name') : t('Orders.Enter_consignee_name')}
                        value={addressData?.companyName || ''}
                        onChangeFunc={handleCompanyNameChange}
                        required
                        disabled={isLoading || isSaving}
                        errorText={duplicateMessage || (showErrors && errors?.companyName ? true : false)}
                        isIconError={!!duplicateMessage}
                    />

                    {/* Address with Open button */}
                    <div className="flex gap-2 items-end">
                        <div className="flex-1">
                            <AutoCompleteGoogle
                                label={isShipper ? t('Orders.Shipper_Address') : t('Orders.Consignee_Address')}
                                labelClassName="block mb-1.5"
                                inputName="fullAddress"
                                placeholder={t('Orders.Enter_address')}
                                className="form_control truncate"
                                mapParentClass="w-full"
                                isClearable
                                onAddressInputChange={handleAddressInputChange}
                                onChangeFunc={handleAutocompleteChange}
                                value={addressData?.fullAddress || ''}
                                errorText={showErrors && (!addressData?.fullAddress || !addressData?.streetNumber) ? true : false}
                                // A red border alone can't explain this one: "Canada" LOOKS filled in, it just
                                // resolved without a street number. The reason rides on the red "i" inside the
                                // field, on hover, instead of a toast (client, 2026-10-09). An empty field stays
                                // border-only, per the required-field rule.
                                errorTextTooltip={showErrors && isAddressIncomplete ? t('Orders.Address_must_be_complete') : ''}
                                disabled={isLoading || isSaving}
                                required
                                showError={false}
                            />
                        </div>
                        <ButtonCmp
                            type="button"
                            className="btn-outline-primary h-[32px]"
                            icon={<ArrowUpRight className="w-4 h-4" />}
                            disabled={isLoading || isSaving}
                            onClick={() => setIsAddressModalOpen(true)}
                        >
                            {t('Common.Open')}
                        </ButtonCmp>
                    </div>

                    {/* Map Preview */}
                    {addressData?.latitude && addressData?.longitude && <div className="rounded-lg overflow-hidden">{MapComponent}</div>}

                    {/* Action buttons for new address */}
                    <div className="flex justify-end gap-2 pt-2">
                        <ButtonCmp
                            type="button"
                            className="btn_secondary_black !text-xs"
                            onClick={() => {
                                setShowErrors(false);
                                onCancelNew();
                            }}
                            disabled={isLoading || isSaving}
                        >
                            {t('Common.Cancel')}
                        </ButtonCmp>
                        <ButtonCmp
                            type="button"
                            className="btn-outline-primary !text-xs"
                            onClick={handleSaveNewAddress}
                            loading={isSaving}
                            disabled={isLoading || isSaving || !addressData?.companyName || !addressData?.fullAddress}
                        >
                            {t('Common.Save')}
                        </ButtonCmp>
                    </div>
                </div>

                {/* Full Address Modal */}
                <Address
                    isOpen={isAddressModalOpen}
                    mode={addressData?.fullAddress ? 'shipperAddress' : 'addressCreate'}
                    handleClose={() => setIsAddressModalOpen(false)}
                    setAddressData={handleAddressModalSave}
                    addressData={addressData}
                    status={!!googleKey}
                    alertMessageShow={true}
                />
            </div>
        );
    }

    // Default: Show dropdown (with or without selection)
    return (
        <SelectBox
            label={label}
            labelClassName="form_label block mb-1.5"
            placeholder={isShipper ? t('Orders.Find_or_add_shipper') : t('Orders.Find_or_add_consignee')}
            options={[
                {
                    label: searchInput ? (isShipper ? 'Saved Shippers' : 'Saved Consignees') : 'Recent',
                    options: formattedOptions,
                },
            ]}
            value={selectedOption}
            onChangeFunc={handleDropdownChange}
            onInputChange={handleInputChange}
            isSearchable={true}
            isClearable={true}
            isDisabled={isLoading}
            isNewOption={true}
            newOptionText={isShipper ? t('Orders.Add_New_Shipper') : t('Orders.Add_New_Consignee')}
            handleCreate={onCreateNew}
            required
            SelectDropdownWrapClass="select-box-shipper-consignee-group-wrap"
        />
    );
};

export default AddressSelectionSection;
